import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import { communicationService } from '../../services/communicationService';
import { crmService } from '../../services/crmService';
import ConversationList from './components/ConversationList';
import MessageThread from './components/MessageThread';
import NewMessageModal from './components/NewMessageModal';
import LoginModal from '../administrative-dashboard/components/LoginModal';

const CommunicationCenter = () => {
  const { variants, transition } = usePageTransition();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Add this block - declare user state
  const [user, setUser] = useState(null);
  
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState(null);
  
  // Modals
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    } else {
      setShowLoginModal(false);
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [conversationsData, clientsData, statsData] = await Promise.all([
        communicationService?.getRecentConversations(50),
        crmService?.getAllLeads({ status: 'active' }),
        communicationService?.getCommunicationStats(7),
      ]);
      
      setConversations(conversationsData || []);
      setClients(clientsData || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading communication data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationMessages = async (clientId) => {
    try {
      const messagesData = await communicationService?.getClientCommunications(clientId);
      setMessages(messagesData || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadConversationMessages(conversation?.client?.id);
  };

  const handleSendMessage = async (messageData) => {
    try {
      await communicationService?.createCommunication(messageData);
      
      if (selectedConversation) {
        loadConversationMessages(selectedConversation?.client?.id);
      }
      
      loadData();
      setShowNewMessageModal(false);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Erreur lors de l\'envoi du message');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery?.trim()) {
      loadData();
      return;
    }

    try {
      const results = await communicationService?.searchCommunications(searchQuery);
      // Group results by client
      const groupedResults = {};
      results?.forEach(msg => {
        const clientId = msg?.clientId;
        if (!groupedResults?.[clientId]) {
          groupedResults[clientId] = {
            client: msg?.client,
            lastMessage: msg,
            messageCount: 0,
          };
        }
        groupedResults[clientId].messageCount++;
      });
      setConversations(Object?.values(groupedResults));
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const filterOptions = [
    { value: 'all', label: 'Toutes les conversations' },
    { value: 'email', label: 'Emails' },
    { value: 'phone', label: 'Appels téléphoniques' },
    { value: 'meeting', label: 'Réunions' },
    { value: 'chat', label: 'Messages instantanés' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <motion.div 
        className="flex-1 pt-28 pb-8 md:pb-12"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Centre de Communication
                </h1>
                <p className="text-muted-foreground">
                  Messagerie centralisée pour la communication client-équipe
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setShowNewMessageModal(true)}>
                  <Icon name="Plus" size={20} className="mr-2" />
                  Nouveau Message
                </Button>
                <Button variant="outline" onClick={() => navigate('/administrative-dashboard')}>
                  <Icon name="ArrowLeft" size={20} className="mr-2" />
                  Retour
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total (7j)</p>
                      <p className="text-2xl md:text-3xl font-bold text-foreground">{stats?.total || 0}</p>
                    </div>
                    <Icon name="MessageSquare" size={40} className="text-primary opacity-20" />
                  </div>
                </div>
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Emails</p>
                      <p className="text-2xl md:text-3xl font-bold text-trust">{stats?.byType?.email || 0}</p>
                    </div>
                    <Icon name="Mail" size={40} className="text-trust opacity-20" />
                  </div>
                </div>
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Appels</p>
                      <p className="text-2xl md:text-3xl font-bold text-success">{stats?.byType?.phone || 0}</p>
                    </div>
                    <Icon name="Phone" size={40} className="text-success opacity-20" />
                  </div>
                </div>
                <div className="bg-card rounded-xl p-4 md:p-6 border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Conversations</p>
                      <p className="text-2xl md:text-3xl font-bold text-warning">{conversations?.length || 0}</p>
                    </div>
                    <Icon name="Users" size={40} className="text-warning opacity-20" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                {/* Search & Filter */}
                <div className="p-4 border-b border-border">
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleSearch} size="sm">
                      <Icon name="Search" size={18} />
                    </Button>
                  </div>
                  <Select
                    options={filterOptions}
                    value={filterType}
                    onChange={(e) => setFilterType(e?.target?.value)}
                    className="w-full"
                  />
                </div>

                {/* Conversations */}
                <ConversationList
                  conversations={conversations}
                  selectedConversation={selectedConversation}
                  onSelectConversation={handleSelectConversation}
                />
              </div>
            </div>

            {/* Message Thread */}
            <div className="lg:col-span-2">
              {selectedConversation ? (
                <MessageThread
                  conversation={selectedConversation}
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  onRefresh={() => loadConversationMessages(selectedConversation?.client?.id)}
                />
              ) : (
                <div className="bg-card rounded-xl border border-border h-64 sm:h-[400px] lg:h-[600px] flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="MessageSquare" size={64} className="text-muted-foreground opacity-20 mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg mb-2">Aucune conversation sélectionnée</p>
                    <p className="text-sm text-muted-foreground">Sélectionnez une conversation pour voir les messages</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      <Footer />
      {/* Modals */}
      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)} 
          onLogin={() => {
            setShowLoginModal(false);
            loadData();
          }} 
        />
      )}
      {showNewMessageModal && (
        <NewMessageModal
          clients={clients}
          onClose={() => setShowNewMessageModal(false)}
          onSend={handleSendMessage}
        />
      )}
    </div>
  );
};

export default CommunicationCenter;